import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { GoogleAuthProvider } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from './PageTheme';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(12px);
`;

const Dialog = styled.form`
  width: min(420px, 100%);
  display: grid;
  gap: 0.9rem;
  padding: clamp(1rem, 4vw, 1.35rem);
  border: 1px solid rgba(226, 232, 240, 0.16);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.98));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42);
`;

const Title = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 1.45rem;
  font-weight: 950;
  line-height: 1.15;
`;

const Copy = styled.p`
  margin: 0;
  color: rgba(203, 213, 225, 0.76);
  font-weight: 750;
  line-height: 1.45;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(248, 113, 113, 0.38);
  border-radius: 16px;
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
  font-weight: 850;
`;

const shouldAskForName = (user) => (
  user &&
  user.isAnonymous !== true &&
  user.email &&
  user.providerId !== GoogleAuthProvider.PROVIDER_ID &&
  !user.displayName &&
  !user.nameOnboardingCompletedAt
);

const NameOnboardingPrompt = () => {
  const { user, updateUserProfile } = useAuth();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (shouldAskForName(user)) {
      setName('');
      setError('');
    }
  }, [user]);

  if (!shouldAskForName(user)) {
    return null;
  }

  const saveProfile = async (displayName) => {
    setIsSaving(true);
    setError('');

    try {
      await updateUserProfile({
        ...(displayName ? { displayName } : {}),
        completeNameOnboarding: true
      });
    } catch (saveError) {
      setError(saveError.response?.data?.message || saveError.message || 'Failed to save name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await saveProfile(name.trim());
  };

  const handleSkip = async () => {
    await saveProfile('');
  };

  return (
    <Overlay>
      <Dialog onSubmit={handleSubmit} role="dialog" aria-modal="true" aria-labelledby="name-onboarding-title">
        <Title id="name-onboarding-title">What name should contacts see?</Title>
        <Copy>
          This is used in missed-pledge emails, and you can change it later in Settings.
        </Copy>
        <Input
          type="text"
          autoComplete="name"
          value={name}
          placeholder="Your name"
          aria-label="Name for accountability emails"
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          disabled={isSaving}
          autoFocus
        />
        <Actions>
          <Button type="submit" disabled={isSaving || !name.trim()}>
            {isSaving ? 'Saving...' : 'Save name'}
          </Button>
          <Button type="button" $secondary onClick={handleSkip} disabled={isSaving}>
            Not now
          </Button>
        </Actions>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Dialog>
    </Overlay>
  );
};

export default NameOnboardingPrompt;
